import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { invoiceKeys, jobKeys } from '@/lib/queryKeys';

/** What a job page reads about money: the job itself, its payments, its totals. */
const JOB_MONEY_SUBKEYS = new Set<unknown>(['payments', 'financial-summary']);

function isJobMoneyQuery(queryKey: QueryKey): boolean {
  const details = jobKeys.details();
  if (queryKey.length < details.length + 1) return false;
  if (!details.every((part, index) => queryKey[index] === part)) return false;
  // `jobKeys.detail(id)` itself, or one of its money sub-queries — not the
  // job's media, visits or activity, which a payment does not change.
  return (
    queryKey.length === details.length + 1 || JOB_MONEY_SUBKEYS.has(queryKey[details.length + 1])
  );
}

/**
 * The other places a payment on an invoice shows, beyond the invoice's own
 * queries: the job page's invoice list (cached per job, outside
 * `invoiceKeys.lists()`), and the job's Payments list and money totals.
 *
 * Every invoice payment door knows the invoice and not its job, so this
 * refreshes those views for every job — cheap, since only the page on screen
 * refetches; the rest are only marked stale. Without it, a bank payment charged
 * from the job page's Record payment went through and the page showed nothing
 * until a hard refresh.
 */
export function invalidateInvoicePaymentViews(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [...invoiceKeys.all(), 'job'] }),
    queryClient.invalidateQueries({ predicate: (query) => isJobMoneyQuery(query.queryKey) }),
  ]);
}
